import {Button, colors, Grid, makeStyles, Switch, TextField, Theme, Typography} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import React, {useState} from 'react';
import _ from 'underscore';
import {OPERATOR, WhereConstraints} from '../../../../graphql/common.datatypes';
import {getHandleFieldChange} from '../../../../utils/formHelpers';
import {WCVDatePicker} from '../../../../components/DateUtilsProvider/WCVDatePicker';
import {formatGraphQlDate} from '../../../../utils/formatters';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    padding: theme.spacing(0, 3),
    width: '98%',
  },
  buttonIcon: {
    marginRight: theme.spacing(1)
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    },
    marginLeft: theme.spacing(1),
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: theme.palette.error.light,
    '&:hover': {
      backgroundColor: theme.palette.error.main
    }
  },
}));

export interface FilterValues {
  search: string
  availableFrom: string | null
  availableTo: string | null
  availableOn: string | null
  minCapacityRemaining: number | null
  minUnitPrice: number | null
  capacityWarning: boolean
  disabled: boolean
  locked: boolean
  allowManualOverbooking: boolean
  showOnInvoice: boolean
}

export const initialFilters = () => ({
  search: '',
  availableFrom: null,
  availableTo: null,
  availableOn: null,
  minCapacityRemaining: null,
  minUnitPrice: null,
  capacityWarning: false,
  disabled: false,
  locked: false,
  allowManualOverbooking: false,
  showOnInvoice: false,
});

export const getResourcesWhereConstraints = (filters: FilterValues) :WhereConstraints => {
  const constraints: WhereConstraints = {AND: [], OR: []};

  if(_.isEqual(filters, initialFilters())){
    constraints.AND!.push({column: 'parent_resource_id', operator: OPERATOR.EQ, value: null});
  }

  if(onlySearchResourceBanks(filters)) {
    //set a filter that will return no results.
    return {AND: [{column: 'parent_resource_id', operator: OPERATOR.EQ, value: -1},]};
  }

  if(filters.search !== '') {
    const block :WhereConstraints = {OR: []};
    block.OR!.push({column: 'display_name', operator: OPERATOR.LIKE, value: `%${filters.search}%`});
    block.OR!.push({column: 'internal_name', operator: OPERATOR.LIKE, value: `%${filters.search}%`});
    block.OR!.push({column: 't_and_c_url', operator: OPERATOR.LIKE, value: `%${filters.search}%`});
    constraints.AND!.push(block);
  }

  constraints.AND!.push({column: 'disabled', operator: OPERATOR.EQ, value: filters.disabled});

  return constraints;
};

export const getResourceBanksWhereConstraints = (filters: FilterValues) :WhereConstraints => {
  const constraints: WhereConstraints = {AND: [], OR: []};

  if (_.isEqual(filters, initialFilters())) {
    return {AND: [{column: 'resource_id', operator: OPERATOR.EQ, value: null},]};
  }

  if (filters.search !== '') {
    const block: WhereConstraints = {OR: []};
    block.OR!.push({column: 'display_name', operator: OPERATOR.LIKE, value: `%${filters.search}%`});
    block.OR!.push({column: 'internal_name', operator: OPERATOR.LIKE, value: `%${filters.search}%`});
    block.OR!.push({column: 'description', operator: OPERATOR.LIKE, value: `%${filters.search}%`});
    constraints.AND!.push(block);
  }

  if (filters.locked) constraints.AND!.push({column: 'locked', operator: OPERATOR.EQ, value: true});
  constraints.AND!.push({column: 'disabled', operator: OPERATOR.EQ, value: filters.disabled});
  if (filters.allowManualOverbooking) constraints.AND!.push({
    column: 'allow_manual_overbooking',
    operator: OPERATOR.EQ,
    value: true
  });
  if (filters.showOnInvoice) constraints.AND!.push({column: 'show_on_invoice', operator: OPERATOR.EQ, value: true});
  //TODO: add available capacity to resource bank table.
  if (filters.minUnitPrice) constraints.AND!.push({
    column: 'unit_price',
    operator: OPERATOR.GTE,
    value: filters.minUnitPrice
  });
  if (filters.availableFrom) constraints.AND!.push({
    column: 'available_from',
    operator: OPERATOR.GTE,
    value: formatGraphQlDate(filters.availableFrom)
  });
  if (filters.availableTo) constraints.AND!.push({
    column: 'available_to',
    operator: OPERATOR.LTE,
    value: formatGraphQlDate(filters.availableTo)
  });

  if (filters.availableOn) {
    const availableOn = formatGraphQlDate(filters.availableOn);
    constraints.AND!.push({column: 'available_from', operator: OPERATOR.LTE, value: availableOn});
    constraints.AND!.push({column: 'available_to', operator: OPERATOR.GTE, value: availableOn});
  }

  return constraints;
};

export const onlySearchResourceBanks = (filters: FilterValues) => {
  if(filters.minCapacityRemaining !== null) return true;
  if(filters.minUnitPrice !== null) return true;
  if(filters.capacityWarning) return true;
  if(filters.locked) return true;
  if(filters.allowManualOverbooking) return true;
  if(filters.showOnInvoice) return true;
  if(filters.availableFrom !== null) return true;
  if(filters.availableTo !== null) return true;
  if(filters.availableOn !== null) return true;

  return false;
};

export const ResourcesSearchFilter = (props: { applyFilters: (filters: FilterValues) => void, filters: FilterValues }) => {
  const {filters} = props;
  const classes = useStyles();
  const [formState, setFormState] = useState({...filters});

  const handleClear = () => {
    props.applyFilters({...initialFilters()});
  };

  const handleFieldChange = getHandleFieldChange(setFormState);

  const applyFilters = () => {
    props.applyFilters(formState);
  };

  return (
    <React.Fragment>

      <Grid container spacing={3} className={classes.content}>
        <Grid item md={12} xs={12}>
          <TextField
            fullWidth
            label="Search"
            name="search"
            onChange={handleFieldChange}
            value={formState.search}
          />
        </Grid>

        <Grid item md={12} xs={12}>
            <Grid container>
              <WCVDatePicker
                  id="availableFrom"
                  label="Available From Starts After:"
                  value={formState.availableFrom}
                  onChange={(date, value) => setFormState({
                    ...formState,
                    availableFrom: value ? value : null
                  })}
              />
              <WCVDatePicker
                  id="availableTo"
                  label="Available To Ends Before:"
                  value={formState.availableTo}
                  onChange={(date, value) => setFormState({
                    ...formState,
                    availableTo: value ? value : null
                  })}
              />
            </Grid>
        </Grid>

        <Grid item md={12} xs={12}>
            <Grid container>
              <WCVDatePicker
                  id="availableOn"
                  label="Available On:"
                  value={formState.availableOn}
                  onChange={(date, value) => setFormState({
                    ...formState,
                    availableOn: value ? value : null
                  })}
              />
            </Grid>
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label="Min Unit Price"
            type="number"
            name="minUnitPrice"
            onChange={handleFieldChange}
            value={formState.minUnitPrice}
          />
        </Grid>

        <Grid item md={12} xs={12}>
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <Switch
                color="secondary"
                edge="start"
                name="disabled"
                value={formState.disabled}
                checked={formState.disabled}
                onChange={handleFieldChange}
              />
            </Grid>
            <Grid item><Typography variant="h5">Disabled?</Typography></Grid>
          </Grid>
        </Grid>

        <Grid item md={12} xs={12}>
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <Switch
                checked={formState.allowManualOverbooking}
                color="secondary"
                edge="start"
                name="allowManualOverbooking"
                onChange={handleFieldChange}
                value={formState.allowManualOverbooking}
              />
            </Grid>
            <Grid item><Typography variant="h5">Allow Manual Overbooking?</Typography></Grid>
          </Grid>
        </Grid>

        {/*<Grid item md={12} xs={12}>*/}
        {/*  <Grid component="label" container alignItems="center" spacing={1}>*/}
        {/*    <Grid item>*/}
        {/*      <Switch*/}
        {/*        checked={formState.capacityWarning}*/}
        {/*        color="secondary"*/}
        {/*        edge="start"*/}
        {/*        name="capacityWarning"*/}
        {/*        onChange={handleFieldChange}*/}
        {/*        value={formState.capacityWarning}*/}
        {/*      />*/}
        {/*    </Grid>*/}
        {/*    <Grid item><Typography variant="h5">Capacity at Warning?</Typography></Grid>*/}
        {/*  </Grid>*/}
        {/*</Grid>*/}

        <Grid item md={12} xs={12}>
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <Switch
                checked={formState.locked}
                color="secondary"
                edge="start"
                name="locked"
                onChange={handleFieldChange}
                value={formState.locked}
              />
            </Grid>
            <Grid item><Typography variant="h5">Is Locked?</Typography></Grid>
          </Grid>
        </Grid>

        <Grid item md={12} xs={12}>
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>
              <Switch
                checked={formState.showOnInvoice}
                color="secondary"
                edge="start"
                name="showOnInvoice"
                onChange={handleFieldChange}
                value={formState.showOnInvoice}
              />
            </Grid>
            <Grid item><Typography variant="h5">Show on Invoice?</Typography></Grid>
          </Grid>
        </Grid>

        <Grid item md={12} xs={12}>
          <Button id="clear" onClick={handleClear} variant="contained">
            <DeleteIcon className={classes.buttonIcon}/>
            Clear
          </Button>
          <Button id="submit" className={classes.saveButton} onClick={applyFilters} variant="contained">
            Search
          </Button>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default ResourcesSearchFilter;